/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-observers, ember/no-runloop, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { service } from '@ember/service';
import { action, computed } from '@ember/object';
import { cancel, debounce } from '@ember/runloop';
import { isEmpty } from '@ember/utils';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import Resource from 'linced-app/enums/enrolment-resource';
import UploadType from 'linced-app/enums/upload-type';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import State from 'linced-app/enums/enrolment-request-state';
import config from 'linced-app/config/environment';
import moment from 'moment';
import { observes } from '@ember-decorators/object';
import { uniq } from '@linc-technologies/linced-common/utils/arrays';

const automaticTermlyUploadOptionKey =
  'nzMOE:automaticUploads:termlyAttendanceSurvey';
const automaticWeeklyUploadOptionKey =
  'nzMOE:automaticUploads:weeklyAttendanceSurvey';
const automaticDailyCollectionOptionKey =
  'nzMOE:automaticUploads:dailyAttendanceCollection';

export default class AttendanceSurveyController extends Controller {
  @action
  setYear(value) {
    this.year = value;
  }

  queryParams = ['year'];

  // Services
  @service
  csv;

  @service
  dates;

  @service
  fetch;

  @service
  options;

  @service
  print;

  @service
  session;

  @service
  store;

  @service
  terms;

  @service
  toaster;

  automaticTermlyUploadOptionKey = automaticTermlyUploadOptionKey;
  automaticWeeklyUploadOptionKey = automaticWeeklyUploadOptionKey;
  automaticDailyCollectionOptionKey = automaticDailyCollectionOptionKey;
  Scopes = Scopes;

  // pollTimer provides an object to periodically poll the enrolment status
  // endpoint while waiting upon the enrolment service receiving snail-mail
  // envelope back from DEX/ENROL.
  @tracked
  pollTimer = null;

  // State provides the enums for the status endpoint's progress on processing
  // a DEX/ENROL response.
  State = State;

  // Status contains the Status response as returned from the enrolment service.
  status = null;

  @tracked year = moment().year();

  @computed('model.terms.[]')
  get years() {
    return uniq(this.model.terms.map((term) => term.year))
      .sort()
      .reverse();
  }

  @tracked
  selectedTerm = null;

  @computed('model.terms.[]', 'year')
  get termsInYear() {
    if (isEmpty(this.model)) {
      return [];
    }
    return this.model.terms
      .filter((item) => item.year === this.year)
      .sort((a, b) => (a.start || '').localeCompare(b.start || ''));
  }

  get minDate() {
    return `${this.year}-01-01`;
  }

  get maxDate() {
    return `${this.year}-12-31`;
  }

  @computed('start', 'end')
  get dateErrors() {
    if (this.start > this.end) {
      return ['Start date must be before end date'];
    }
    return [];
  }

  @tracked
  processing = false;

  // Daily Attendance Collection
  @tracked dailyAttendanceCollectionStart = moment()
    .subtract(27, 'days')
    .format('YYYY-MM-DD');

  @tracked dailyAttendanceCollectionEnd = moment().format('YYYY-MM-DD');

  get dateErrorsDailyCollection() {
    let start = moment(this.dailyAttendanceCollectionStart);
    let end = moment(this.dailyAttendanceCollectionEnd);
    let now = moment();

    let errors = [];

    if (start.isAfter(end)) {
      errors.push('Start date must be before end date');
    }
    if (end.isAfter(now)) {
      errors.push("End date can't be after today");
    }
    if (end.diff(start, 'days') > 27) {
      errors.push('file must not contain more than 28 days');
    }

    return errors;
  }

  // Attendance Corrections

  // A corrections file's collection period must end more than 28 calendar days in the past.
  @tracked attendanceCorrectionEnd = moment()
    .subtract(29, 'days')
    .format('YYYY-MM-DD');

  // The extract file must not contain more than 60 days' worth of data.
  // Defaulting this to 59 days before attendanceCorrectionEnd
  @tracked attendanceCorrectionStart = moment()
    .subtract(29, 'days')
    .subtract(59, 'days')
    .format('YYYY-MM-DD');

  get dateErrorsAttendanceCorrection() {
    let start = moment(this.attendanceCorrectionStart);
    let end = moment(this.attendanceCorrectionEnd);
    let now = moment();

    let errors = [];

    if (start.isAfter(end)) {
      errors.push('Start date must be before end date');
    }
    if (end.diff(start, 'days') > 59) {
      errors.push('Corrections file must not contain more than 60 days');
    }
    if (end.isAfter(now.subtract(29, 'days'))) {
      errors.push(
        'Collection period must end more than 28 calendar days in the past',
      );
    }

    return errors;
  }

  // Weekly Attendance Survey
  monday = moment().day('Monday').format('YYYY-MM-DD');

  friday = moment().day('Friday').format('YYYY-MM-DD');
  currentYear = moment().year();
  @tracked
  processingUpload = false;
  @tracked
  uploadIsWeekly = false;
  @tracked
  uploadIsTermly = false;
  @tracked
  uploadIsDaily = false;
  @tracked
  uploadIsCorrections = false;
  @tracked
  uploadConfirmationOpen = false;
  @tracked
  weeklyConfirmationOpen = false;
  @tracked
  dailyConfirmationOpen = false;
  @tracked
  correctionsConfirmationOpen = false;
  @optionAlias(automaticTermlyUploadOptionKey)
  automaticTermlyUpload;
  @optionAlias(automaticWeeklyUploadOptionKey)
  automaticWeeklyUpload;
  @optionAlias(automaticDailyCollectionOptionKey)
  automaticDailyCollection;

  @tracked
  end;

  @tracked
  start;

  @computed('model.surveys.@each.{fromDate,toDate,dexInfo,state}')
  get weeklySurveySent() {
    let filtered = this.model.surveys.filter((upload) => {
      return upload.fromDate === this.monday && upload.toDate === this.friday;
    });
    return filtered.some((upload) => {
      return upload.state === State.READY && upload.dexInfo === 'OK';
    });
  }

  @observes('model.uploadStatuses.[]', 'status', 'status.status')
  pollStatus() {
    if (!isEmpty(this.status)) {
      if (this.status.status <= State.PROCESSING) {
        // We have a Status (request), and it has not yet been processed.
        this.pollTimer = debounce(this, 'updateStatus', 2000, false);
      } else if (!isEmpty(this.pollTimer)) {
        // We've got a result; cancel any pending status poll.
        cancel(this.pollTimer);
        this.pollTimer = null;
      }
    } else if (!isEmpty(this.model.uploadStatuses)) {
      // Check if we received an in-flight request.
      let inFlight = this.model.uploadStatuses.filter((status) => {
        return status.status <= State.PROCESSING;
      });
      if (!isEmpty(inFlight)) {
        this.set('status', inFlight[0]);
        this.processingUpload = true;
      }
    }
  }

  updateStatus() {
    if (isEmpty(this.status)) {
      return;
    }
    this.status.reload().then((status) => {
      this.checkStatus(status);
    });
  }

  checkStatus(status) {
    switch (status.status) {
      case State.READY:
        // We're done.  Reload the list of surveys to get the latest state.
        this.model.surveys.update().then(() => {
          // Toast for success or failure.
          let survey = this.model.surveys.find(
            (item) => item.status_id === status.id,
          );
          if (!isEmpty(survey)) {
            if (survey.state === State.READY && survey.dexInfo === 'OK') {
              this.toaster.success('Attendance survey uploaded successfully.');
            } else {
              this.toaster.error('Error uploading attendance survey.');
            }
          }
          // Clear processing flag.
          this.processingUpload = false;
        });
        break;

      case State.PROCESSING:
        // Still processing - queue another update.
        this.pollTimer = debounce(this, 'updateStatus', 2000, false);
        break;

      case State.FAILED:
        this.toaster.error('Error uploading attendance survey.');
        // Clear processing flag.
        this.processingUpload = false;
        break;
    }
  }

  generateSurvey(startDate, endDate, isDailyCollection, isCorrectionsFile) {
    let payload = {
      schoolId: this.session.school.id,
      startDate,
      endDate,
      isDailyCollection,
      isCorrectionsFile,
    };
    let options = {
      data: JSON.stringify(payload),
    };
    return this.fetch.post(
      `${config.APP.apiHost}/attendance/v4/surveys/nzmoe`,
      options,
    );
  }

  checkTerm() {
    if (isEmpty(this.selectedTerm)) {
      // No term selected - check if the new dates match one.
      let term = this.termsInYear.find((term) => {
        return term.start === this.start && term.end === this.end;
      });
      if (!isEmpty(term)) {
        this.selectedTerm = term;
      }
    } else {
      if (
        this.selectedTerm.start !== this.start ||
        this.selectedTerm.end !== this.end
      ) {
        this.selectedTerm = null;
      }
    }
  }

  reset() {
    let bounds = {
      start: `${this.year}-01-01`,
      end: moment().year(this.year).format('YYYY-MM-DD'),
      selectedTerm: null,
    };

    if (this.year !== moment().year()) {
      // Default to the full year for past years, because defaulting to
      // "until today last year" doesn't make much sense.
      bounds.end = `${this.year}-12-31`;
    }

    if (!isEmpty(this.termsInYear)) {
      // If we have terms, default to the first term for the year.
      let term = this.termsInYear[0];
      let currentTerm = this.terms.currentTerm;
      if (this.termsInYear.includes(currentTerm)) {
        // Default to the current term, as it's in range.
        term = currentTerm;
      }

      if (!isEmpty(term)) {
        bounds = {
          start: term.start,
          end: term.end,
          selectedTerm: term,
        };
      }
    }

    this.setProperties(bounds);
  }

  @action
  selectTerm(term) {
    this.selectedTerm = term;
    this.start = term.start;
    this.end = term.end;
  }

  @action
  setStartDate(start) {
    this.start = start;
    this.checkTerm();
  }

  @action
  setEndDate(end) {
    this.end = end;
    this.checkTerm();
  }

  @action
  setStartCollectionDate(start) {
    this.dailyAttendanceCollectionStart = start;
  }

  @action
  setEndCollectionDate(end) {
    this.dailyAttendanceCollectionEnd = end;
  }

  @action
  setStartCorrectionDate(start) {
    this.attendanceCorrectionStart = start;
  }

  @action
  setEndCorrectionDate(end) {
    this.attendanceCorrectionEnd = end;
  }

  @action
  downloadCsv(start, end, isDailyCollection, isCorrectionsFile) {
    this.processing = true;

    this.generateSurvey(start, end, isDailyCollection, isCorrectionsFile)
      .then((resp) => {
        this.print.downloadRawCsv(resp.fileName, resp.surveyData);
      })
      .catch(() => {
        this.toaster.error(
          'Attendance survey generation failed.  Please try again later.',
        );
      })
      .finally(() => {
        this.processing = false;
      });
  }

  @action
  sendSurvey(
    from,
    to,
    isWeekly,
    isTermly,
    isDailyCollection,
    isCorrectionsFile,
  ) {
    this.processingUpload = true;
    this.uploadIsWeekly = isWeekly;
    this.uploadIsTermly = isTermly;
    this.uploadIsDaily = isDailyCollection;
    this.uploadIsCorrections = isCorrectionsFile;
    this.uploadConfirmationOpen = false;
    this.weeklyConfirmationOpen = false;
    this.dailyConfirmationOpen = false;
    this.correctionsConfirmationOpen = false;

    this.generateSurvey(from, to, isDailyCollection, isCorrectionsFile).then(
      (survey) => {
        let uploadType = isCorrectionsFile
          ? UploadType.CORRECTION
          : isDailyCollection
            ? UploadType.DAILY
            : UploadType.SURVEY;
        let upload = {
          requesterUsername: this.session.user.username,
          providerId: this.session.school.jurisdictionalId,
          fromDate: from,
          toDate: to,
          surveyContent: survey.surveyData,
          uploadType: uploadType,
        };

        let status = this.store.createRecord('enrolments/status', {
          schoolId: this.session.school,
          requesterId: this.session.person,
          jurisdiction: Jurisdiction.NZ_MOE,
          date: moment().format('YYYY-MM-DD'),
          resource: isCorrectionsFile
            ? Resource.ATTENDANCE_CORRECTIONS
            : Resource.ATTENDANCE_SURVEYS,
          nzMoe: [upload],
        });
        this.set('status', status);
        return status
          .save()
          .then(() => {
            // Reload list of "uploads".
            return this.model.uploadStatuses.update().then(() => {
              // Check the status, in case it's already done.  Otherwise, the
              // pollStatus observer will cancel any timers and skip polling.
              this.checkStatus(this.status);
            });
          })
          .catch(() => {
            this.processingUpload = false;
            this.toaster.error("Couldn't send attendance survey.");
          });
      },
    );
  }
}
